import { Factory, trait } from 'miragejs';
import { faker } from '@faker-js/faker';
import { capitalize } from '@ember/string';

export default Factory.extend({
  name(i) {
    return `Zone ${i}`;
  },

  accentColor: null,
  coverPhoto: null,
  locale: 'en',

  asProperty: trait({
    address: () => {
      const address = {
        addressLineOne: faker.location.streetAddress(),
        city: faker.location.city(),
        state: faker.location.state({ abbreviated: true }),
        zip: faker.location.zipCode(),
        latitude: faker.location.latitude(),
        longitude: faker.location.longitude(),
        time_zone: 'UTC',
      };
      address.address = `${address.addressLineOne}, ${address.city}, ${address.state} ${address.zip}`;
      return address;
    },
    name: () => `The ${capitalize(faker.lorem.word())} Building`,
    parent: null,
    afterCreate(property, server) {
      // the "I have a QR code"/"I don't have a QR code" flows are automatically created on the backend
      // in the real app; there should never be a case (outside of testing) where they don't exist, which
      // is why they're created here and not via a `withFlows` trait.
      const withQRCodeFlow = server.create('flow', 'asPropertyWithQRCode', {
        enabled: true,
      });
      const withoutQRCodeFlow = server.create('flow', 'asPropertyWithoutQRCode', {
        enabled: true,
      });

      server.create('config', {
        property,
        flows: [withQRCodeFlow, withoutQRCodeFlow],
      });

      server.create('connect-property-configuration', {
        property,
        inviteApprovalsEnabled: false,
      });

      server.create('badge', {
        property,
        showPhoto: false,
        extraFieldOne: 'none',
        extraFieldTwo: 'none',
        extraFieldThree: 'none',
      });
    },
  }),

  asSuite: trait({
    name: (i) => `Suite ${i}`,
    // Generate area as random square footage and convert to square inches. Avoids ending up
    // with randomly-generated square inch values that don't convert cleanly to square feet.
    area: () => faker.number.int({ min: 100, max: 35000 }) * 144,
  }),

  withCoverPhoto: trait({
    coverPhoto: 'filename.jpg',
    coverPhotoUrl: '/assets/images/covid-card-welcome-bg.png',
  }),

  withLogo: trait({
    logo: 'https://picsum.photos/600/600',
  }),

  withTenants: trait({
    afterCreate(zone, server) {
      const tenants = [
        ...server.createList('tenant', 2, 'asConnected', 'withLogo', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asConnected', 'withSuites', 'employeesReadDisabled', {
          property: zone,
        }),

        server.create('tenant', 'asInvited', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asDisconnected', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asInactive', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asInvited', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asInviteExpired', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asNotInvited', 'withSuites', {
          property: zone,
        }),
      ];
      zone.update({ tenants });
    },
  }),

  withTenantsWithInvites: trait({
    afterCreate(zone, server) {
      const tenants = [
        ...server.createList('tenant', 2, 'asConnected', 'withLogo', 'withSuites', 'withInvites', {
          property: zone,
        }),

        server.create('tenant', 'asConnected', 'withSuites', 'employeesReadDisabled', {
          property: zone,
        }),

        server.create('tenant', 'asInvited', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asDisconnected', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asInactive', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asInvited', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asInviteExpired', 'withSuites', {
          property: zone,
        }),

        server.create('tenant', 'asNotInvited', 'withSuites', {
          property: zone,
        }),
      ];

      zone.update({ tenants });
    },
  }),

  withSuites: trait({
    afterCreate(zone, server) {
      server.createList('zone', 4, 'asSuite', {
        parent: zone,
      });
    },
  }),

  withBlockList: trait({
    afterCreate(zone, server) {
      server.createList('blacklist-filter', 4, {
        property: zone,
      });
    },
  }),

  withDeviceConfigStaticPages: trait({
    afterCreate(property, server) {
      server.create('device-config-static-page', 'asAssistance', {
        property,
      });

      server.create('device-config-static-page', 'asWalkUp', {
        property,
      });

      server.create('device-config-static-page', 'asInvited', {
        property,
      });
    },
  }),

  afterCreate(zone, server) {
    if (!zone.company) {
      const company =
        server.schema.companies.first() ??
        server.create('company', {
          name: 'Cool Chip',
        });
      zone.update({ company });
    }
  },
});
